import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/utils/cn';

/**
 * Tailwind classes match v1 patterns (see `Clinio/src/pages/clinic/Login.tsx`)
 * — same focus-ring, disabled state, and dark-mode treatment.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary:
          'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 border border-transparent',
        secondary:
          'bg-white text-gray-900 hover:bg-gray-50 focus:ring-indigo-500 border border-gray-300 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600',
        danger:
          'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 border border-transparent',
        ghost:
          'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-indigo-500 border border-transparent dark:text-gray-200 dark:hover:bg-gray-700 shadow-none',
      },
      size: {
        sm: 'px-3 py-1.5 text-xs',
        md: 'px-4 py-2',
        lg: 'px-5 py-2.5 text-base',
        full: 'w-full px-4 py-2',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
});
